import { clearImpersonation, impersonateAs } from '@/auth/impersonation.js';
import { useSession } from '@/auth/useSession.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { Separator } from '@/components/ui/separator.js';
import { RoleBadgeList } from '@/components/users/role-badge.js';
import { EmptyArray, emptyQueryOptions, getUsersTypeahead } from '@/lib/api-calls.js';
import { useDebouncedInput } from '@/lib/common-utils.js';

import { isAdminOnlyUser } from '@airreading/common';
import { GetUsersTypeaheadModifier, typeaheadPagesModifier } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { LoaderIcon } from 'lucide-react';
import { ReactNode, useId, useState } from 'react';

export interface ImpersonationSwitcherProps {
  trigger: ReactNode;
}

export const ImpersonationSwitcher = ({ trigger }: ImpersonationSwitcherProps) => {
  const [query, setQuery] = useState<string | undefined>();
  const { loggedInUser, actingUser, isImpersonating } = useSession();
  const [[liveQuery, debouncedQuery], setDebouncedQuery] = useDebouncedInput(query, setQuery);
  const req = {
    ...typeaheadPagesModifier,
    filters: {
      typeahead: debouncedQuery,
    },
  } satisfies GetUsersTypeaheadModifier;
  const { data: response, isPending } = useQuery(isImpersonating ? emptyQueryOptions : getUsersTypeahead(req));
  const inputId = useId();
  const users = (response || EmptyArray).filter((user) => user.id !== loggedInUser.id);

  const impersonationSelection = (
    <>
      <div className="p-2">
        <Label className={'font-bold'} htmlFor={inputId}>
          Impersonate as user:
        </Label>
        <Input
          onChange={(e) => setDebouncedQuery(e.target.value)}
          value={liveQuery}
          id={inputId}
          placeholder="Search user by name or email"
        />
      </div>
      <Separator className="my-2" />
      <div className="py-2">
        {isPending ? (
          <div className="flex justify-center">
            <LoaderIcon />
          </div>
        ) : (
          <div className="">
            {users.map((user) => {
              const isBlocked = isAdminOnlyUser(user);
              return (
                <Button
                  variant={'ghost'}
                  className="w-full min-w-0 h-auto flex justify-between py-2 mb-2"
                  key={user.id}
                  disabled={isBlocked}
                  onClick={() => impersonateAs(user.id)}
                >
                  <div className="space-y-1 flex min-w-0 flex-col items-start whitespace-normal">
                    <p className="text-sm font-medium leading-none break-words [overflow-wrap:anywhere] text-left">
                      {user.fullNameOrEmail}
                    </p>
                    {user.fullNameOrEmail !== user.email && (
                      <p className="text-sm text-muted-foreground break-words [overflow-wrap:anywhere] text-left">
                        {user.email}
                      </p>
                    )}
                    {isBlocked && <p className="text-xs text-destructive">Cannot impersonate admin-only users</p>}
                  </div>
                  <div className="ml-auto shrink-0 font-medium flex items-center gap-1">
                    <RoleBadgeList userRoles={user.userRoles} />
                  </div>
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Popover modal={isImpersonating}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        collisionPadding={8}
        updatePositionStrategy="always"
        className="w-[min(40rem,calc(100vw-1.5rem))] max-h-[min(28rem,calc(100dvh-1.5rem))] overflow-y-auto"
      >
        <div className="flex flex-col">
          {isImpersonating ? (
            <div className="space-y-3">
              <p className="break-words [overflow-wrap:anywhere]">
                You are viewing the app as <b>{actingUser.fullNameOrEmail}</b>
              </p>
              <Button type="button" onClick={() => clearImpersonation()}>
                Stop Impersonation
              </Button>
            </div>
          ) : (
            impersonationSelection
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
